var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { TablePagination, Toolbar, useMediaQuery, } from '@mui/material';
import { useTranslate, useListPaginationContext, sanitizeListRestProps, ComponentPropType, } from 'ra-core';
import { PaginationActions } from './PaginationActions';
import { PaginationLimit } from './PaginationLimit';
export var Pagination = memo(function (props) {
    var _a = props.rowsPerPageOptions, rowsPerPageOptions = _a === void 0 ? DefaultRowsPerPageOptions : _a, actions = props.actions, _b = props.limit, limit = _b === void 0 ? DefaultLimit : _b, rest = __rest(props, ["rowsPerPageOptions", "actions", "limit"]);
    var _c = useListPaginationContext(props), isLoading = _c.isLoading, hasNextPage = _c.hasNextPage, page = _c.page, perPage = _c.perPage, total = _c.total, setPage = _c.setPage, setPerPage = _c.setPerPage;
    var translate = useTranslate();
    var isSmall = useMediaQuery(function (theme) {
        return theme.breakpoints.down('md');
    });
    var totalPages = useMemo(function () {
        return total != null ? Math.ceil(total / perPage) : undefined;
    }, [perPage, total]);
    /**
     * Warning: MUI's page is 0-based
     */
    var handlePageChange = useCallback(function (event, page) {
        event && event.stopPropagation();
        if (page < 0 || page > totalPages - 1) {
            throw new Error(translate('ra.navigation.page_out_of_boundaries', {
                page: page + 1,
            }));
        }
        setPage(page + 1);
    }, [totalPages, setPage, translate]);
    var handlePerPageChange = useCallback(function (event) {
        setPerPage(event.target.value);
    }, [setPerPage]);
    var labelDisplayedRows = useCallback(function (_a) {
        var from = _a.from, to = _a.to, count = _a.count;
        return count === -1 && hasNextPage
            ? translate('ra.navigation.partial_page_range_info', {
                offsetBegin: from,
                offsetEnd: to,
                _: "%{from}-%{to} of more than %{to}",
            })
            : translate('ra.navigation.page_range_info', {
                offsetBegin: from,
                offsetEnd: to,
                total: count === -1 ? to : count,
                _: "%{from}-%{to} of %{count === -1 ? to : count}",
            });
    }, [translate, hasNextPage]);
    var labelItem = useCallback(function (type) { return translate("ra.navigation.".concat(type), { _: "Go to ".concat(type, " page") }); }, [translate]);
    if (isLoading) {
        return React.createElement(Toolbar, { variant: "dense" });
    }
    // Avoid rendering TablePagination if "page" value is invalid
    if (total === 0 || page < 1 || (total != null && page > totalPages)) {
        return limit;
    }
    if (isSmall) {
        return (React.createElement(TablePagination, __assign({ count: total == null ? -1 : total, rowsPerPage: perPage, page: page - 1, onPageChange: handlePageChange, rowsPerPageOptions: emptyArray, component: "span", labelDisplayedRows: labelDisplayedRows }, sanitizeListRestProps(rest))));
    }
    var ActionsComponent = actions
        ? actions // overridden by caller
        : !isLoading && total != null
            ? PaginationActions // regular navigation
            : undefined; // partial navigation (uses default TablePaginationActions)
    return (React.createElement(TablePagination, __assign({ count: total == null ? -1 : total, rowsPerPage: perPage, page: page - 1, onPageChange: handlePageChange, onRowsPerPageChange: handlePerPageChange, 
        // @ts-ignore
        ActionsComponent: ActionsComponent, nextIconButtonProps: {
            disabled: !hasNextPage,
        }, component: "span", labelRowsPerPage: translate('ra.navigation.page_rows_per_page'), labelDisplayedRows: labelDisplayedRows, getItemAriaLabel: labelItem, rowsPerPageOptions: rowsPerPageOptions }, sanitizeListRestProps(rest))));
});
Pagination.propTypes = {
    actions: ComponentPropType,
    limit: PropTypes.element,
    rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
};
var DefaultLimit = React.createElement(PaginationLimit, null);
var DefaultRowsPerPageOptions = [5, 10, 25, 50];
var emptyArray = [];
//# sourceMappingURL=Pagination.js.map